import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getUserEducation } from '../../../../actions/licensesAction';
import { fetchCurrentUser } from '../../../../actions/userActions';
import { createLoadingSelector } from '../../../../apis/selectors';
import { useGetPostingDetails } from '../../../../domain/PermanentJobs/useCases';
import { UserType } from '../../../../enums/UserType';
import history from '../../../../history';
import { useToggle } from '../../../../hooks/useToggle';
import { Colors } from '../../../../themes/colors';
import { showErrorMessage } from '../../../../utils';
import { TabViewContainer } from '../../../commonComponents/TabViewContainer';
import { PlansCombobox, RecruitersCombobox } from '../components';
import { CompensationBenefitsCard } from './components/CompensationBenefitsCard';
import { ConfirmationPopup } from './components/ConfirmationPopup';
import { GeneralInformationCard } from './components/GeneralInformationCard';
import { JobDescriptionCard } from './components/JobDescriptionCard';
import { OfficeSelectionCard } from './components/OfficeSelectionCard';
import { ScheduleInformationCard } from './components/ScheduleInformationCard';
import { SuccessPopup } from './components/SuccessPopup';
import { usePostPermanentJobForm } from './hooks/usePostPermanentJobForm';
import { transformFromPostingDetailsEntityToFormData } from './utils/PostingDetailsMapper';
import './styles.scss';

const loadingSelector = createLoadingSelector([
  'GET_ALL_METADATA',
  'GET_ALL_PROFESSIONS',
  'GET_USER_EDUCATION',
]);

export const PostPermanentJobV2 = ({ location }) => {
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const office = location?.state?.office;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isConfirmationOpen, controlsConfirmation] = useToggle();
  const currentOffice = useSelector((state) => state.users.currentUser);

  const { data } = useGetPostingDetails(
    { postingId: params.get('postingId') },
    { enabled: params.get('postingId') != null },
  );

  const {
    createdPostingData,
    errorStatus,
    formValues,
    mutation,
    handleChange,
    handleJobDescriptionChange,
    handleProfessionChange,
    setFormValues,
  } = usePostPermanentJobForm({ office });

  const isInitialLoading = useSelector((state) => loadingSelector(state));

  useEffect(() => {
    dispatch(getUserEducation({ userType: UserType.DNT }));
  }, [dispatch]);

  useEffect(() => {
    if (data?.officeSelected && data?.officeId) {
      dispatch(fetchCurrentUser(data.officeId, { init: true }));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (data) {
      setFormValues((prev) => ({
        ...prev,
        ...transformFromPostingDetailsEntityToFormData(data),
      }));
    }
  }, [data, setFormValues]);

  useEffect(() => {
    if (params.get('postingId') != null && currentOffice) {
      setFormValues((prev) => ({
        ...prev,
        office: currentOffice,
      }));
    }
  }, [currentOffice, params, setFormValues]);

  if (isInitialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
        <Spinner
          animation="border"
          role="status"
          variant="info"
          style={{
            height: 30,
            width: 30,
            marginLeft: 20,
            marginTop: 10,
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflowY: 'auto',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'center',
        backgroundColor: Colors.background,
        paddingBottom: 80,
      }}
    >
      <TabViewContainer
        title="Post a Permanent Job"
        style={{
          width: '95%',
          maxWidth: 800,
          padding: 0,
        }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 230,
            }}
          >
            <p className="bold">Recruiter:</p>
            <RecruitersCombobox
              lang={{
                allOptionsText: 'No Recruiter Selected',
              }}
              keyExtractor={(it) => it.email}
              value={formValues.recruiterEmail}
              onChange={(val) => handleChange('recruiterEmail', val.label)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 230,
            }}
          >
            <p className="bold">Plan:</p>
            <PlansCombobox
              isError={isSubmitted && errorStatus?.invalidPaymentPlan}
              lang={{
                allOptionsText: 'No Plan Selected',
              }}
              value={formValues.planId}
              onChange={(val) => handleChange('planId', val.value)}
            />
          </div>
        </div>

        <OfficeSelectionCard formValues={formValues} handleChange={handleChange} />

        <GeneralInformationCard
          formValues={formValues}
          handleChange={handleChange}
          handleProfessionChange={handleProfessionChange}
          errorStatus={isSubmitted ? errorStatus : {}}
        />

        <CompensationBenefitsCard
          formValues={formValues}
          handleChange={handleChange}
          errorStatus={isSubmitted ? errorStatus : {}}
        />

        <ScheduleInformationCard
          formValues={formValues}
          handleChange={handleChange}
          errorStatus={isSubmitted ? errorStatus : {}}
        />

        <JobDescriptionCard
          formValues={formValues}
          handleChange={handleJobDescriptionChange}
          errorStatus={isSubmitted ? errorStatus : {}}
        />

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <button
            className="btn btn-success"
            disabled={mutation.isPending}
            style={{ height: 48, width: 200, marginTop: 20, alignSelf: 'center' }}
            type="button"
            onClick={() => {
              if (errorStatus.invalidForm) {
                showErrorMessage({ message: 'Error: Missing Required Information' });
                setIsSubmitted(true);
                return;
              }

              controlsConfirmation.setOn();
            }}
          >
            Post Permanent Job
          </button>
        </div>
      </TabViewContainer>
      {isConfirmationOpen && (
        <ConfirmationPopup
          controls={controlsConfirmation}
          isSubmitting={mutation.isPending}
          onConfirm={() => {
            mutation.mutate(
              {
                ...formValues,
                officeId: formValues.office?.id,
                postingId: params.get('postingId') || undefined,
              },
              {
                onSuccess: () => {
                  setIsSubmitted(false);
                },
              },
            );
          }}
        />
      )}
      {mutation.isSuccess && (
        <SuccessPopup
          onOk={() => {
            controlsConfirmation.setOff();
            mutation.reset();

            const { officeId, postingId } = createdPostingData.current;

            if (officeId && postingId) {
              history.push(`/permanent-job/office/${officeId}/job/${postingId}`);
            }
          }}
        />
      )}
    </div>
  );
};
